import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatTimecode } from "@infoflyt/shared/timecode";
import { Stepper } from "../../components/Stepper";
import { dekrypter } from "../../state/krypto";
import { lesUtkast, skrivUtkast, slettUtkast } from "../../state/utkast";
import { useSession } from "../../state/SessionProvider";
import * as su from "../../state/su";
import * as db from "../../state/db";
import { generateReport, shareReport } from "../../report/pdf";
import * as rapportsynk from "../../state/rapportsynk";
import { reportStem } from "../../report/filename";
import { confirm as hapticConfirm, heavy, tap as hapticTap } from "../../components/haptics";
import * as logolager from "../../state/logolager";

/**
 * Klippeprogram og markørfil.
 *
 * Hjulet er tegnet i designet, men funksjonen hører til fase 7 — eksport til
 * klipp. En kontroll som ikke gjør noe er verre enn ingen kontroll for en som
 * avslutter dagen klokka tre om natta, så flaten bygges i varianten uten.
 * Skru den på her når eksportørene finnes. Se docs/10-slagplan.md.
 */
const KLIPPEPROGRAM_ENABLED = false;

const SKIFT = ["Dag", "Midt", "Kveld", "Natt"] as const;
const p2 = (n: number): string => String(n).padStart(2, "0");

/** Ti dager bakover og tre fram — loggen avsluttes samme dag eller dagen etter. */
function datoer(): { label: string; iso: string }[] {
  const base = new Date();
  const out: { label: string; iso: string }[] = [];
  for (let i = -6; i <= 3; i++) {
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + i);
    out.push({
      label: `${p2(d.getDate())}.${p2(d.getMonth() + 1)}`,
      iso: `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`,
    });
  }
  return out;
}

type Phase = "form" | "making" | "done";

/**
 * Avslutt dagen.
 *
 * Brukes én gang per logg, sent i et langt skift, av en som er sliten.
 * «Avslutt dagen» skal være lett å finne og vanskelig å treffe ved uhell —
 * derfor egen trykkbekreftelse, ikke dialogboks. Se briefen i
 * `Prosjektet logg-flat design (1)/uploads/brief-avslutt-dagen.md`.
 */
/**
 * Hva flata sier naar rapporten ikke kom fram.
 *
 * Én setning per grunn, og ingen av dem lyver: «venter paa nett» om noe som
 * ligger og venter paa at loggen skal gaa opp er et galt svar, og regi kan
 * ikke gjoere noe med et galt svar.
 */
const VENTETEKST: Record<rapportsynk.Grunn, string> = {
  "uten-nett": "Rapporten venter på nett og går til produksjonen så snart "
    + "linja er der.",
  "venter-paa-loggen": "Rapporten går opp så snart loggen har kommet opp. "
    + "Den henger på dagens logg, og den går først.",
  avvist: "Rapporten ble avvist av serveren og ligger i kø. Den prøver igjen.",
  "fila-borte": "PDF-en ble ikke funnet lokalt, og rapporten må lages på nytt. "
    + "Trykk «Endre rapporten».",
};

export function AvsluttPane({ onBackToLog }: { onBackToLog(): void }): JSX.Element {
  const {
    entries, svar, anchor, spec, closeLog, production, member, shootDay, unitName,
    unitId, oektId,
  } = useSession();

  /*
   * HVOR RAPPORTEN SKAL.
   *
   * Hoerer loggen til en PRODUKSJON, leveres den dit — redaktoeren finner
   * den paa nettflata, og ingen trenger aa velge en mottaker. Er den
   * PRIVAT, finnes det ingen produksjon aa levere til: det er brukerens
   * egen logg, og hen sender den selv gjennom delingsarket.
   */
  const privat = production.id === "privat" || production.id === "";
  const dates = useMemo(datoer, []);

  /*
   * Sjekklista til rapporten, lest LOKALT.
   *
   * Rapporten lages paa telefonen uten at noen server er involvert, og da er
   * det den lokale lista som er fasiten — den samme flata har vist hele
   * dagen. Hva som BLE stilt leses av loggpostene inne i `generateReport`;
   * her trengs bare spoersmaalene og hvem de gjelder.
   */
  const [suData, setSuData] = useState<su.SuData>(su.TOM_SU);
  /**
   * Hva som er stilt ET ANNET STED, med navn og dag.
   *
   * Rapporten skiller tre tilstander: stilt i denne loggen, stilt av en
   * annen foer rapporten gikk, og ikke stilt. Brukeren 03.11.2026. Lest
   * LOKALT, som sjekklista — rapporten lages ofte uten dekning, og navnet
   * kom ned med forrige synk.
   */
  const [ellers, setEllers] = useState<readonly {
    question: string; participant: string; hvem: string; dag: string;
  }[]>([]);
  useEffect(() => {
    let avbrutt = false;
    void (async () => {
      try {
        const d = await su.lesLokalt(production.id);
        if (!avbrutt) setSuData(d);
      } catch { /* rapporten lages uten sjekklista */ }
      try {
        await db.openDb(production.id);
        const e = await db.lastStiltMedHvem(production.id);
        if (!avbrutt) setEllers(e);
      } catch { /* da staar det «ikke stilt enda», som foer */ }
    })();
    return () => { avbrutt = true; };
  }, [production.id]);

  /*
   * Startverdiene kommer fra utkastet om det finnes.
   *
   * Leses i initialisatoren og ikke i en effekt: en effekt ville foerst tegnet
   * et tomt felt og saa fylt det, og det ville lest som at alt var tapt det
   * halve sekundet det tok.
   */
  const utkast = useMemo(() => lesUtkast(production.id, shootDay), []);

  /*
   * Oppsummeringen er kryptert og maa dekrypteres asynkront, mens resten av
   * utkastet vises med én gang. `henter` er sann bare naar det FINNES en
   * kryptert tekst aa vente paa — uten utkast rendres skjemaet som foer, uten
   * blink.
   */
  const [summary, setSummary] = useState(
    utkast === null || utkast.kryptert ? "" : utkast.summary,
  );
  const [henter, setHenter] = useState(utkast !== null && utkast.kryptert);

  useEffect(() => {
    if (utkast === null || !utkast.kryptert) return;
    let avbrutt = false;
    void dekrypter(utkast.summary).then((t) => {
      if (avbrutt) return;
      setSummary(t);
      setHenter(false);
    });
    return () => { avbrutt = true; };
  }, [utkast]);
  const [dagIdx, setDagIdx] = useState(
    utkast !== null
      ? Math.max(0, utkast.dag - 1)
      : Math.max(0, Number(shootDay) - 1),
  );
  const [datoIdx, setDatoIdx] = useState(() => {
    // Fra dato og ikke fra indeks: lista bygges relativt til i dag, saa en
    // indeks peker paa en annen dato etter midnatt.
    const i = utkast === null ? -1 : dates.findIndex((d) => d.iso === utkast.dateIso);
    return i >= 0 ? i : 6;
  });
  const [skiftIdx, setSkiftIdx] = useState(() => {
    const i = utkast === null ? -1 : SKIFT.indexOf(utkast.skift as (typeof SKIFT)[number]);
    return i >= 0 ? i : 2;
  });
  const [enhet, setEnhet] = useState(utkast?.enhet ?? unitName);
  const [enhetFocus, setEnhetFocus] = useState(false);

  const [phase, setPhase] = useState<Phase>("form");
  /** Vises bare naar delingen feilet av en annen grunn enn avbrudd. */
  const [feil, setFeil] = useState<string | null>(null);
  const [armed, setArmed] = useState(false);
  /* Rapporten ligger i koe. Dagen er avsluttet, men den er ikke framme hos
     redaktoer ennaa — og det skal staa. */
  const [venter, setVenter] = useState(false);
  /* HVORFOR den venter. Flata gjettet «paa nett» foer, ogsaa med full
     dekning. Se state/rapportsynk.ts. */
  const [grunn, setGrunn] = useState<rapportsynk.Grunn | null>(null);
  /* Fikk telefonen skrevet PDF-en? Er den false og rapporten venter, er
     rapporten borte om appen avsluttes. Det maa staa paa skjermen. */
  const [lokalt, setLokalt] = useState(true);
  const [version, setVersion] = useState(1);
  const armTimer = useRef<number | null>(null);
  const enhetEl = useRef<HTMLInputElement | null>(null);

  /*
   * ========  VERDIEN, IKKE FELTET  ========
   *
   * Her laa en feil jeg lagde selv for en time siden: da enheten ble
   * arvet fra teamet, ble SKRIVEFELTET byttet mot en etikett — men
   * porten og rapporten leste fortsatt `enhet`, altsaa tilstanden. Var
   * den tom (et utkast fra foer teamet ble satt, eller en foerste
   * gjengiving foer navnet var lest ned), sto det «Skriv inn enhet» over
   * et felt som ikke gikk an aa skrive i. Brukeren 02.11.2026: «enhet er
   * allerede fylt og ikke mulig aa redigere».
   *
   * Teamet vinner naar det finnes. Da kan de to ikke komme i utakt.
   */
  const enhetVerdi = unitName !== "" ? unitName : enhet;
  const enhetOk = enhetVerdi.trim().length > 0;

  /*
   * ========  INGENTING USENDT NAAR RAPPORTEN SKRIVES  ========
   *
   * Brukeren 29.10.2026: «hvis de ikke er paa nett skal det synkes opp foer
   * rapporten skrives.»
   *
   * Grunnen er at rapporten er et OEYEBLIKKSBILDE. Den bygges av postene
   * denne telefonen har akkurat naa, og den sendes som PDF. Ligger
   * fotografens poster igjen i hens utboks, staar de ikke i rapporten — og
   * de kommer heller ikke inn i den etterpaa. Redaktoeren faar en rapport
   * som ser komplett ut og ikke er det, og ingenting sier fra.
   *
   * Derfor: paa et team, med noe i koen, er knappen stengt. Den sier hvor
   * mange rader som venter, saa det er en tilstand og ikke en vegg.
   *
   * Uten team gjelder den ikke. Da er loggen din alene, og det du ser er
   * alt som finnes.
   */
  const [iKoe, setIKoe] = useState(0);
  const paaLag = unitId !== "";
  useEffect(() => {
    if (!paaLag) return;
    let avbrutt = false;
    const se = (): void => {
      void db.loggkoen(production.id).then((rader: readonly unknown[]) => {
        if (!avbrutt) setIKoe(rader.length);
      }).catch(() => { /* lokal lesing. Feiler den, staar tallet. */ });
    };
    se();
    const id = window.setInterval(se, 2000);
    return () => { avbrutt = true; window.clearInterval(id); };
  }, [paaLag, production.id]);

  const koenTom = !paaLag || iKoe === 0;
  const dateEntry = dates[datoIdx];
  const shift = SKIFT[skiftIdx] ?? SKIFT[0];

  const naming = {
    production: production.name,
    shootDay: String(dagIdx + 1),
    dateIso: dateEntry?.iso ?? "",
    unit: enhetVerdi,
    shift,
    initials: member.initials,
    version,
  };
  const filename = `${reportStem(naming)}.pdf`;

  const tips = ["LET 1", "LET 2", "LET 3", "Kontrollrom A"].filter(
    (u) => u !== enhet.trim() && u.toLowerCase().startsWith(enhet.trim().toLowerCase()),
  );

  /*
   * Utkastet skrives ved hver endring.
   *
   * Ingen demping: dette er fire smaa felt og én tekst, og en demping ville
   * nettopp mistet de siste tastetrykkene — som er dem man er reddest for.
   * Skrivingen er den samme jobben nettleseren gjoer for et skjemafelt.
   *
   * Ikke mens panelet viser kvitteringen: da er rapporten laget og utkastet
   * slettet, og en skriving her ville satt det inn igjen.
   */
  useEffect(() => {
    if (phase === "done") return;
    /*
     * IKKE SKRIV MENS VI VENTER PAA DEKRYPTERINGEN.
     *
     * Effekten kjoerer ogsaa ved montering, og da staar `summary` tom. Uten
     * denne vakta ville den foerste skrivingen lagret en tom oppsummering over
     * utkastet — altsaa slettet nettopp det vi holder paa aa hente fram.
     */
    if (henter) return;
    void skrivUtkast(production.id, {
      shootDay: shootDay,
      summary,
      dag: dagIdx + 1,
      dateIso: dateEntry?.iso ?? "",
      skift: shift,
      enhet: enhetVerdi,
    });
  }, [phase, henter, summary, dagIdx, dateEntry, shift, enhetVerdi]);

  const anchorSetAt = anchor === null
    ? "—"
    : new Date(anchor.wallUtcMs).toLocaleTimeString("no-NO", { hour: "2-digit", minute: "2-digit" });

  /* Panelet avmonteres hver gang menyen lukkes eller man gaar tilbake til
     rotnivaa. Uret som armerer en handling som ikke kan gjoeres om skal
     ikke overleve det. */
  useEffect(() => () => {
    if (armTimer.current !== null) window.clearTimeout(armTimer.current);
  }, []);

  const run = useCallback(async (): Promise<void> => {
    if (anchor === null) return;
    setPhase("making");
    setFeil(null);
    // Et pust, slik at «Lager PDF …» faktisk rekker å bli sett.
    await new Promise((r) => setTimeout(r, 120));
    /* Logoen leses fra disk FOER PDF-en bygges: `generateReport` er
       synkron, og den er det fordi den kalles ved skiftslutt og ikke skal
       kunne henge paa noe. Ligger ingen logo der, blir rapporten uten —
       det er ikke en feil som skal stoppe leveringen. */
    const logoDataUrl = await logolager.dataUrl(production.logoSti);
    const report = generateReport({
      production,
      logoDataUrl,
      member,
      anchor,
      spec,
      entries,
      summary,
      naming,
      anchorSetAtLocal: anchorSetAt,
      deltakere: suData.deltakere,
      spoersmaal: suData.spoersmaal,
      svar,
      stiltEllers: ellers.map((e) => ({
        spoersmaalId: e.question, deltakerId: e.participant,
        hvem: e.hvem, opptaksdag: e.dag,
      })),
    });
    /*
     * Avbrutt deling er ikke en feil, men den maa foere tilbake.
     *
     * navigator.share avviser med AbortError naar delingsarket lukkes — det
     * er det letteste som finnes aa gjoere ved et uhell. Uten dette slapp
     * avvisningen ut, fasen ble staaende paa «making», og knappen sa «Lager
     * PDF …» for alltid: rapporten kunne ikke lages uten aa starte appen paa
     * nytt.
     *
     * Loggen lukkes bare naar rapporten faktisk kom ut.
     */
    if (privat) {
      /*
       * PRIVAT: brukeren sender den selv.
       *
       * Avbrutt deling er ikke en feil, men den maa foere tilbake.
       * navigator.share avviser med AbortError naar delingsarket lukkes —
       * det er det letteste som finnes aa gjoere ved et uhell. Uten dette
       * slapp avvisningen ut, fasen ble staaende paa «making», og knappen
       * sa «Lager PDF …» for alltid.
       *
       * Loggen lukkes bare naar rapporten faktisk kom ut.
       */
      try {
        await shareReport(report);
      } catch (err) {
        const avbrutt = err instanceof DOMException && err.name === "AbortError";
        setPhase("form");
        if (!avbrutt) {
          setFeil(err instanceof Error ? err.message : "Rapporten kunne ikke deles.");
        }
        return;
      }
    } else {
      /*
       * PRODUKSJON: rapporten leveres, og dagen avsluttes uansett.
       *
       * Her er regelen en annen enn for privat, og det er med vilje. Uten
       * dekning ville «loggen lukkes bare naar rapporten kom ut» holdt
       * dagen aapen til regi fant en linje — og en dag som ikke lar seg
       * avslutte klokka tre om natta er verre enn en rapport som ligger i
       * koe. Koen gaar opp av seg selv, og flata sier fra at den venter.
       */
      const utfall = await rapportsynk.lever(
        { produksjonId: production.id }, {
        sessionId: oektId ?? "",
        pdf: report.blob,
        meta: { ...naming, oppsummering: undefined },
        oppsummering: summary,
        });
      setVenter(!utfall.levert);
      setGrunn(utfall.grunn);
      setLokalt(utfall.lokalt);
      /* Nummeret kommer fra tjeneren. Flata skal vise det den FIKK, ikke
         det den gjettet paa. */
      if (utfall.versjon !== null) setVersion(utfall.versjon);
    }
    // Rapporten er ute. Utkastet har gjort jobben sin.
    slettUtkast(production.id);
    // Loggen er lukket. Endrer regi rapporten senere, øker versjonen.
    closeLog();
    heavy();
    setPhase("done");
  }, [anchor, spec, entries, summary, naming, anchorSetAt, closeLog, privat, oektId, production.id, member.id, suData, ellers, svar]);

  const onAction = (): void => {
    if (phase === "making") return;
    if (!enhetOk) { enhetEl.current?.focus(); return; }
    /* Teamets poster maa vaere oppe. Se `koenTom`. */
    if (!koenTom) return;
    if (!armed) {
      // Første trykk armerer. Knappen går tilbake av seg selv.
      hapticConfirm();
      setArmed(true);
      if (armTimer.current !== null) window.clearTimeout(armTimer.current);
      armTimer.current = window.setTimeout(() => setArmed(false), 4000);
      return;
    }
    if (armTimer.current !== null) window.clearTimeout(armTimer.current);
    setArmed(false);
    void run();
  };

  /*
   * SKJERMEN RETTER SEG SELV.
   *
   * «Rapporten venter» var et oeyeblikksbilde som ble staaende: gikk koen
   * opp to sekunder senere — og det er det vanlige, for loggen gaar foerst —
   * sto det fortsatt «venter paa nett» paa en rapport redaktoeren alt hadde.
   * Takten her er den samme som App.tsx bruker paa koen.
   */
  useEffect(() => {
    if (phase !== "done" || !venter || privat) return;
    let avbrutt = false;
    const id = window.setInterval(() => {
      void (async () => {
        const igjen = await rapportsynk.ventende(production.id);
        if (avbrutt || igjen > 0) return;
        const nummer = await rapportsynk.levertVersjon(oektId ?? "");
        if (avbrutt) return;
        if (nummer !== null) setVersion(nummer);
        setVenter(false);
        setGrunn(null);
      })();
    }, 3000);
    return () => { avbrutt = true; window.clearInterval(id); };
  }, [phase, venter, privat, production.id, oektId]);

  if (phase === "done") {
    return (
      <div className="avslutt">
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <span style={{ fontSize: 17, fontWeight: 500, color: "var(--ink)" }}>
            {privat
              ? `Rapport v${version} er klar`
              : venter
                ? "Rapporten er laget"
                : `Rapport v${version} er levert`}
          </span>
          <span style={{ fontSize: 13, lineHeight: 1.6, color: "var(--ink-dim)" }}>
            {privat
              ? "Loggen er lukket."
              : venter
                ? `Loggen er lukket. ${VENTETEKST[grunn ?? "uten-nett"]}`
                : `Loggen er lukket. Rapporten er levert til ${production.name}.`}
            {privat || venter
              ? " Endrer du rapporten senere, lages den på nytt."
              : ` Endrer du rapporten senere, får neste fil v${version + 1}.`}
          </span>
          {/*
            * ORDENE FRA TJENEREN, NAAR DEN SA NEI.
            *
            * `console.error` finnes ikke paa en telefon. En avvist levering
            * sa «venter paa nett» og ingenting mer, og den eneste veien til
            * aa finne ut hva som gikk galt var aa gjenskape det paa en
            * maskin. Staar det her, kan regi lese det opp.
            */}
          {venter && grunn === "avvist" && rapportsynk.klagen() !== "" ? (
            <span className="avslutt__klage">{rapportsynk.klagen()}</span>
          ) : null}
          {venter && !lokalt ? (
            <span className="avslutt__klage">
              Telefonen fikk ikke lagret PDF-en. Lukk ikke appen før rapporten
              er levert — da må dagen avsluttes på nytt.
            </span>
          ) : null}
        </div>
        <span className="avslutt__filename">{filename}</span>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/*
            * «Send paa nytt» hoerer til PRIVAT.
            *
            * Er loggen en produksjons, er rapporten levert — da er det aa
            * sende den igjen ikke en handling som mangler, det er en som
            * ikke betyr noe. Vil regi rette noe, er det «Endre rapporten»
            * under: den gir en ny versjon, og den leveres paa nytt.
            */}
          {privat ? (
            <button
              type="button"
              className="avslutt__action"
              onClick={() => { void run(); }}
            >
              Del rapport på nytt
            </button>
          ) : null}

      {feil === null ? null : (
        <span
          className="mono"
          style={{ fontSize: 12, lineHeight: 1.5, color: "var(--state-bad)" }}
        >
          {feil}
        </span>
      )}
          <button
            type="button"
            style={{ minHeight: 44, fontSize: 13.5, color: "var(--ink-dim)", textAlign: "left" }}
            onClick={() => { setVersion((v) => v + 1); setPhase("form"); }}
          >
            Endre rapporten
          </button>
          <button
            type="button"
            style={{ minHeight: 44, fontSize: 13.5, color: "var(--ink-dim)", textAlign: "left" }}
            onClick={onBackToLog}
          >
            Tilbake til loggen
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="avslutt">
        {/* Ingen generert tekst, ingen mal og ingen overskrifter i feltet. */}
        <textarea
          className="avslutt__summary"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder={henter ? "Henter utkast …" : "Skriv oppsummeringen for redaktøren"}
          rows={4}
        />

        <div className="avslutt__valg">
          <Stepper
            label="Opptaksdag"
            value={String(dagIdx + 1)}
            kanNed={dagIdx > 0}
            kanOpp={dagIdx < 59}
            onStep={(d) => setDagIdx((v) => Math.max(0, Math.min(59, v + d)))}
          />
          <Stepper
            label="Dato"
            value={dateEntry?.label ?? ""}
            kanNed={datoIdx > 0}
            kanOpp={datoIdx < dates.length - 1}
            onStep={(d) => setDatoIdx((v) => Math.max(0, Math.min(dates.length - 1, v + d)))}
          />

          <div className="avslutt__field">
            <span className="avslutt__label">Skift</span>
            <div className="segment" role="group" aria-label="Skift">
              {SKIFT.map((sk, i) => (
                <button
                  key={sk}
                  type="button"
                  className={`segment__valg${i === skiftIdx ? " segment__valg--valgt" : ""}`}
                  aria-pressed={i === skiftIdx}
                  onClick={() => { hapticTap(); setSkiftIdx(i); }}
                >
                  {sk}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/*
          * ========  ENHETEN ARVES NAAR DEN FINNES  ========
          *
          * Teamet er satt i menyen og foelger medlemsraden. Staar det
          * der, er det ikke noe aa velge — brukeren 02.11.2026: «det er
          * allerede definert, saa dette feltet kan arve den verdien.»
          *
          * Feltet staar igjen som et SKRIVEFELT for den som ikke er paa
          * et team: en VJ alene eller en regi paa en liten produksjon
          * maa fortsatt kunne si hvilken enhet rapporten gjelder.
          */}
        {unitName !== "" ? (
          <div className="avslutt__field">
            <span className="avslutt__label">Enhet</span>
            <span className="avslutt__arvet mono">{unitName}</span>
            <span className="avslutt__merknad">
              Fra teamet ditt. Endres i menyen under Fagfunksjon og team.
            </span>
          </div>
        ) : (
        <div className="avslutt__field">
          <span className="avslutt__label">Enhet</span>
          <input
            ref={enhetEl}
            className="avslutt__input"
            value={enhet}
            onChange={(e) => setEnhet(e.target.value)}
            onFocus={() => setEnhetFocus(true)}
            onBlur={() => window.setTimeout(() => setEnhetFocus(false), 120)}
            placeholder="LET 2"
          />
          {/* Enhetene som finnes i produksjonen fra før. Et trykk fyller inn. */}
          {enhetFocus && tips.length > 0 ? (
            <div className="avslutt__tips">
              {tips.map((u) => (
                <button
                  key={u}
                  type="button"
                  className="avslutt__tip"
                  onPointerDown={(e) => { e.preventDefault(); setEnhet(u); }}
                >
                  {u}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        )}

        {KLIPPEPROGRAM_ENABLED ? <ProgramWheelPlaceholder /> : null}

        {/* Kun til lesing: det som havner i rapporten uten at regi gjør noe. */}
        <div className="avslutt__field">
          <span className="avslutt__label" style={{ color: "var(--ink-faint)" }}>
            Kommer med automatisk
          </span>
          <div className="avslutt__auto">
            <span>Produksjon</span><span>{production.name}</span>
            <span>Logget av</span><span>{member.displayName}</span>
            <span>Framerate</span><span>{spec.rate.name} fps</span>
            <span>Tidskode satt</span>
            <span>
              {anchorSetAt} → {anchor === null ? "—" : formatTimecode(anchor.tcFrames, spec)}
            </span>
            <span>Poster</span><span>{entries.length}</span>
          </div>
        </div>

        <span className="avslutt__filename">{filename}</span>
      </div>

      <button
        type="button"
        className={[
          "avslutt__action",
          armed ? "avslutt__action--armed" : "",
          !enhetOk || !koenTom ? "avslutt__action--needs" : "",
          phase === "making" ? "avslutt__action--busy" : "",
        ].filter(Boolean).join(" ")}
        onClick={onAction}
      >
        {phase === "making"
          ? "Lager PDF …"
          : !enhetOk
            ? "Skriv inn enhet"
            : !koenTom
              ? `Venter på ${iKoe} rad${iKoe === 1 ? "" : "er"} opp`
            : armed
              ? "Trykk igjen for å lukke loggen"
              : "Lukk loggen og lag rapport"}
      </button>
    </>
  );
}

function ProgramWheelPlaceholder(): JSX.Element {
  return (
    <div className="avslutt__field">
      <span className="avslutt__label">Klippeprogram</span>
      <span className="avslutt__filename">
        Markørfil til Avid, Premiere og Resolve kommer i eksportfasen.
      </span>
    </div>
  );
}
